import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  CloudOff,
  ExternalLink,
  History,
  Loader2,
  MessageCircle,
  Rocket,
  Zap,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  FLOW_STATUS_LABEL,
  type FlowDetail,
  type FlowIssueView,
  type FlowSettings,
  type FlowTheme,
} from "@rn/contracts";
import { flowVariables, validateFlow, type FlowGraph } from "@rn/core/flow";
import { nodeLabel, slugProblem } from "@/components/flows/blocks";
import { FlowPreview } from "@/components/flows/FlowPreview";
import { GraphEditor } from "@/components/flows/graph/GraphEditor";
import { withPositions } from "@/components/flows/graph/graph";
import { useGraphHistory } from "@/components/flows/graph/useGraphHistory";
import { ReportPanel } from "@/components/flows/ReportPanel";
import { SessionsPanel } from "@/components/flows/SessionsPanel";
import { SettingsFields } from "@/components/flows/SettingsFields";
import { SharePanel } from "@/components/flows/SharePanel";
import { ThemeFields } from "@/components/flows/ThemeFields";
import { useFlowAutosave, validTheme, type SaveState } from "@/components/flows/useFlowAutosave";
import { VersionsDialog } from "@/components/flows/VersionsDialog";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Hint } from "@/components/ui/tooltip";
import { ApiError } from "@/lib/api";
import {
  asFlowGraph,
  errorMessage,
  publishIssues,
  uploadFlowMedia,
  useFlow,
  usePublishFlow,
} from "@/lib/flows";

export const Route = createFileRoute("/_authenticated/typechat/$flowId")({
  component: EditorPage,
});

const TOM_STATUS: Record<FlowDetail["status"], NonNullable<BadgeProps["variant"]>> = {
  published: "success",
  draft: "muted",
  archived: "outline",
};

/**
 * Editor do Typechat.
 *
 * Mesmo esqueleto do editor de Formulários (cabeçalho de uma linha, abas,
 * salvamento automático, publicar travado pelo `validateFlow`); muda a aba
 * principal, que aqui é o quadro de blocos ligados por setas.
 */
function EditorPage() {
  const { flowId } = Route.useParams();
  const flow = useFlow(flowId);
  // Restaurar uma versão troca o rascunho inteiro: remontar o editor é mais
  // simples (e mais seguro) do que sincronizar cada estado local.
  const [rodada, setRodada] = useState(0);

  if (flow.isPending) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex shrink-0 items-center gap-3 border-b px-4 py-3 sm:px-6">
          <Skeleton className="size-8" />
          <Skeleton className="h-6 w-56" />
          <Skeleton className="ml-auto h-9 w-28" />
        </div>
        <Skeleton className="m-4 min-h-0 flex-1 rounded-2xl" />
      </div>
    );
  }

  if (flow.isError || !flow.data) {
    const naoExiste = flow.error instanceof ApiError && flow.error.status === 404;
    return (
      <EmptyState
        icon={AlertCircle}
        title={naoExiste ? "Typechat não encontrado" : "Não foi possível abrir o Typechat"}
        description={
          naoExiste
            ? "Ele pode ter sido excluído, ou o link é de outra empresa."
            : "Confira a conexão e tente de novo."
        }
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/typechat">
              <ArrowLeft />
              Voltar para a lista
            </Link>
          </Button>
        }
      />
    );
  }

  return (
    <ChatEditor
      key={`${flowId}:${rodada}`}
      initial={flow.data}
      onReload={() => setRodada((r) => r + 1)}
    />
  );
}

function ChatEditor({ initial, onReload }: { initial: FlowDetail; onReload: () => void }) {
  const flowId = initial.id;
  // O cache recebe a resposta de cada PATCH/publicação: status, versão e
  // "alterações não publicadas" vêm daqui; o conteúdo, do estado local.
  const live = useFlow(flowId).data ?? initial;

  const [name, setName] = useState(initial.name);
  const [slug, setSlug] = useState(initial.slug);
  const [theme, setTheme] = useState<FlowTheme>(initial.theme ?? {});
  const [settings, setSettings] = useState<FlowSettings>(initial.settings ?? {});
  const [versoes, setVersoes] = useState(false);

  const { estado, queue, flush, slugEmUso } = useFlowAutosave(flowId);
  const publicar = usePublishFlow(flowId);

  // Blocos sem posição (modelo criado no servidor) ganham a do layout uma vez,
  // ao abrir; o desenho é salvo na primeira edição.
  const inicial = useMemo(() => withPositions(asFlowGraph(initial.draft)), [initial.draft]);
  const history = useGraphHistory(inicial, (g: FlowGraph) => {
    // Grafo vazio não passa no servidor (precisa de início); fica só local
    // até a pessoa pôr o primeiro bloco.
    if (Object.keys(g.nodes).length > 0) queue({ draft: g });
  });
  const graph = history.graph;

  const variaveis = useMemo(() => flowVariables(graph), [graph]);
  const issues: FlowIssueView[] = useMemo(() => validateFlow(graph), [graph]);
  const erros = issues.filter((i) => i.level === "error");
  const porBloco = useMemo(() => {
    const m = new Map<string, FlowIssueView[]>();
    for (const i of issues) if (i.nodeId) m.set(i.nodeId, [...(m.get(i.nodeId) ?? []), i]);
    return m;
  }, [issues]);
  const gerais = useMemo(() => issues.filter((i) => !i.nodeId), [issues]);

  // ── Mudanças ──────────────────────────────────────────────────────────────

  const mudarTema = (t: FlowTheme) => {
    setTheme(t);
    queue({ theme: validTheme(t) });
  };

  const mudarAjustes = (s: FlowSettings) => {
    setSettings(s);
    queue({ settings: s });
  };

  const mudarNome = (n: string) => {
    setName(n);
    if (n.trim()) queue({ name: n.trim() });
  };

  const erroSlug = slugProblem(slug) ?? (slug === live.slug ? null : slugEmUso);
  const mudarSlug = (s: string) => {
    setSlug(s);
    if (!slugProblem(s)) queue({ slug: s });
  };

  const executarPublicacao = async () => {
    await flush();
    try {
      const f = await publicar.mutateAsync();
      toast.success(`Publicado — versão ${f.publishedVersion ?? ""}`.trim(), {
        description: f.publicUrl,
        action: { label: "Abrir", onClick: () => window.open(f.publicUrl, "_blank", "noopener") },
      });
    } catch (e) {
      const pendencias = publishIssues(e).filter((i) => i.level === "error");
      toast.error(
        pendencias.length > 0
          ? `Não publicou: ${pendencias.map((p) => p.message).join(" · ")}`
          : errorMessage(e, "Não foi possível publicar"),
      );
    }
  };

  // ── Derivados da tela ─────────────────────────────────────────────────────

  const salvando = estado === "pending" || estado === "saving";
  const naoPublicado =
    live.status === "published" && (live.hasUnpublishedChanges || salvando || estado === "error");
  const vazio = Object.keys(graph.nodes).length === 0;
  const podePublicar =
    erros.length === 0 &&
    !vazio &&
    live.status !== "archived" &&
    (live.status !== "published" || naoPublicado);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Tabs defaultValue="fluxo" className="flex min-h-0 flex-1 flex-col">
        {/* ── Cabeçalho ──────────────────────────────────────────────────── */}
        <div className="shrink-0 border-b px-4 pt-3 sm:px-6">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <Hint label="Voltar para a lista">
              <Button asChild size="icon-sm" variant="ghost" aria-label="Voltar para a lista">
                <Link to="/typechat">
                  <ArrowLeft />
                </Link>
              </Button>
            </Hint>
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <input
                value={name}
                aria-label="Nome do Typechat"
                maxLength={120}
                onChange={(e) => mudarNome(e.target.value)}
                className="min-w-0 flex-1 truncate rounded-md bg-transparent px-1.5 py-1 font-display text-base font-semibold tracking-tight transition-colors hover:bg-muted focus-visible:bg-card focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30 sm:max-w-sm sm:text-lg"
              />
              <Badge variant="soft" className="hidden sm:inline-flex">
                Typechat
              </Badge>
              <Badge variant={TOM_STATUS[live.status]}>
                {FLOW_STATUS_LABEL[live.status]}
                {live.publishedVersion ? ` · v${live.publishedVersion}` : ""}
              </Badge>
              {naoPublicado && (
                <Badge variant="warning" className="hidden md:inline-flex">
                  alterações não publicadas
                </Badge>
              )}
              <EstadoSalvo estado={estado} onRetry={() => void flush()} />
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              <Hint label="Versões publicadas">
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Versões publicadas"
                  onClick={() => setVersoes(true)}
                >
                  <History />
                </Button>
              </Hint>
              {live.publishedVersion ? (
                <Button asChild size="sm" variant="outline">
                  <a href={live.publicUrl} target="_blank" rel="noreferrer">
                    <ExternalLink />
                    <span className="hidden sm:inline">Ver página</span>
                  </a>
                </Button>
              ) : (
                <Hint label="Publique primeiro para a página existir">
                  <span tabIndex={0} className="inline-flex rounded-md">
                    <Button size="sm" variant="outline" disabled>
                      <ExternalLink />
                      <span className="hidden sm:inline">Ver página</span>
                    </Button>
                  </span>
                </Hint>
              )}
              <Hint
                label={
                  erros.length > 0 ? (
                    <span className="block space-y-0.5">
                      <span className="block font-semibold">Corrija antes de publicar:</span>
                      {erros.slice(0, 6).map((e, i) => {
                        const bloco = e.nodeId ? graph.nodes[e.nodeId] : undefined;
                        return (
                          <span key={i} className="block">
                            • {bloco ? `${nodeLabel(bloco).slice(0, 40)} — ` : ""}
                            {e.message}
                          </span>
                        );
                      })}
                      {erros.length > 6 && (
                        <span className="block">e mais {erros.length - 6}…</span>
                      )}
                    </span>
                  ) : live.status === "archived" ? (
                    "Arquivado: duplique para voltar a usar"
                  ) : !podePublicar ? (
                    "Nada novo para publicar"
                  ) : (
                    "Põe o rascunho no ar como uma versão nova"
                  )
                }
              >
                <span tabIndex={podePublicar ? -1 : 0} className="inline-flex rounded-md">
                  <Button
                    size="sm"
                    disabled={!podePublicar || publicar.isPending}
                    onClick={() => void executarPublicacao()}
                  >
                    {publicar.isPending ? <Loader2 className="animate-spin" /> : <Rocket />}
                    {publicar.isPending ? "Publicando…" : "Publicar"}
                    {erros.length > 0 && (
                      <span className="rounded-full bg-primary-foreground/20 px-1.5 text-[10px] tabular-nums">
                        {erros.length}
                      </span>
                    )}
                  </Button>
                </span>
              </Hint>
            </div>
          </div>

          <TabsList className="mt-2 border-b-0">
            <TabsTrigger value="fluxo">Fluxo</TabsTrigger>
            <TabsTrigger value="tema">Tema</TabsTrigger>
            <TabsTrigger value="ajustes">Ajustes</TabsTrigger>
            <TabsTrigger value="compartilhar">Compartilhar</TabsTrigger>
            <TabsTrigger value="conversas">Conversas</TabsTrigger>
            <TabsTrigger value="relatorio">Relatório</TabsTrigger>
          </TabsList>
        </div>

        {/* ── Fluxo ──────────────────────────────────────────────────────── */}
        <TabsContent value="fluxo" className="flex min-h-0 flex-1 flex-col pt-0">
          <GraphEditor
            history={history}
            theme={theme}
            variables={variaveis}
            issuesByNode={porBloco}
            generalIssues={gerais}
          />
        </TabsContent>

        {/* ── Tema ───────────────────────────────────────────────────────── */}
        <TabsContent value="tema" className="min-h-0 flex-1 pt-0">
          <div className="grid h-full min-h-0 grid-rows-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_minmax(18rem,22rem)]">
            <div className="min-h-0 overflow-y-auto px-4 py-5 sm:px-6">
              <div className="mx-auto max-w-2xl">
                <ThemeFields theme={theme} onChange={mudarTema} onUploadMedia={uploadFlowMedia} />
              </div>
            </div>
            <aside className="hidden min-h-0 border-l bg-muted/20 p-4 lg:block">
              <FlowPreview graph={graph} theme={theme} kind="chat" mode="chat" />
            </aside>
          </div>
        </TabsContent>

        {/* ── Ajustes ────────────────────────────────────────────────────── */}
        <TabsContent value="ajustes" className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
          <div className="mx-auto max-w-2xl">
            <SettingsFields
              kind="chat"
              settings={settings}
              onChange={mudarAjustes}
              slug={slug}
              onSlugChange={mudarSlug}
              slugError={erroSlug}
              publicUrl={live.publicUrl}
              published={live.publishedVersion !== null}
            />
          </div>
        </TabsContent>

        <TabsContent
          value="compartilhar"
          className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-5 sm:px-6"
        >
          <NoWhatsapp keywords={settings.whatsapp?.startKeywords ?? []} />
          <SharePanel
            flowId={flowId}
            kind="chat"
            publicUrl={live.publicUrl}
            slug={live.slug}
            published={live.publishedVersion !== null}
          />
        </TabsContent>

        <TabsContent value="conversas" className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
          <SessionsPanel flowId={flowId} graph={graph} kind="chat" />
        </TabsContent>

        <TabsContent value="relatorio" className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
          <ReportPanel flowId={flowId} graph={graph} />
        </TabsContent>
      </Tabs>

      <VersionsDialog
        flowId={flowId}
        currentVersion={live.publishedVersion}
        open={versoes}
        onOpenChange={setVersoes}
        beforeRestore={flush}
        onRestored={onReload}
      />
    </div>
  );
}

/** Como o Typechat começa no WhatsApp: palavra-chave ou regra de automação. */
function NoWhatsapp({ keywords }: { keywords: string[] }) {
  return (
    <Card className="mx-auto max-w-3xl">
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>No WhatsApp</CardTitle>
          <CardDescription>
            No WhatsApp não há link: o Typechat começa dentro da conversa que já existe.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex items-start gap-3">
          <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <MessageCircle className="size-4" />
          </span>
          <div className="min-w-0">
            <p className="font-medium">Por palavra-chave</p>
            <p className="text-xs text-muted-foreground">
              {keywords.length > 0 ? (
                <>
                  Quem escrever{" "}
                  {keywords.map((k, i) => (
                    <span key={k}>
                      {i > 0 && (i === keywords.length - 1 ? " ou " : ", ")}
                      <strong className="text-foreground">“{k}”</strong>
                    </span>
                  ))}{" "}
                  inicia a conversa. Dá para pôr a palavra no texto pronto de um anúncio de clique
                  para o WhatsApp.
                </>
              ) : (
                "Nenhuma palavra configurada. Defina em Ajustes › WhatsApp — por exemplo, “avaliação”."
              )}
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Zap className="size-4" />
          </span>
          <div className="min-w-0">
            <p className="font-medium">Por uma regra de Automação</p>
            <p className="text-xs text-muted-foreground">
              Em{" "}
              <Link to="/automacao" className="font-medium text-primary hover:underline">
                Automação
              </Link>
              , crie uma regra (por exemplo, “lead chegou de anúncio”) com o passo{" "}
              <strong className="text-foreground">Iniciar Typechat</strong>.
            </p>
          </div>
        </div>
        <p className="rounded-lg bg-info/12 px-3 py-2 text-xs text-info">
          Se alguém da clínica responder a conversa — pelo CRM ou pelo celular —, o robô se cala
          nela e a pessoa fica com a equipe.
        </p>
      </CardContent>
    </Card>
  );
}

/** "Salvando…", "Salvo" ou "Não salvo — tentar de novo", ao lado do nome. */
function EstadoSalvo({ estado, onRetry }: { estado: SaveState; onRetry: () => void }) {
  if (estado === "idle") return null;
  if (estado === "error") {
    return (
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex shrink-0 cursor-pointer items-center gap-1 text-xs font-medium text-destructive hover:underline"
      >
        <CloudOff className="size-3.5" />
        <span className="hidden sm:inline">Não salvo — tentar de novo</span>
      </button>
    );
  }
  const salvando = estado === "pending" || estado === "saving";
  return (
    <span
      aria-live="polite"
      className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground"
    >
      {salvando ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
      <span className="hidden sm:inline">{salvando ? "Salvando…" : "Salvo"}</span>
    </span>
  );
}
